import React, { useCallback, useEffect, useRef } from 'react';

import Popover from '@repo/editor/src/components/Popover/Popover';
import {
  PopoverAlign,
  PopoverPlacement,
} from '@repo/editor/src/components/Popover/types';
import { withEventScope } from '@repo/event-processor';
import { useDebounce } from '@repo/react-hooks/useDebounce';
import { useIntersectionObserver } from '@repo/react-hooks/useIntersectionObserver';

import { SuggestionList } from '../SuggestionList/SuggestionList';
import { Item, Mention, MentionItem } from '../types';
import {
  afterSeparators,
  convertIntoItems,
  findItemPositionBorder,
  getItemInPosition,
  getText,
  matchMentionItems,
  splitByMentions,
  updateItemInPosition,
} from '../utils';

import { Input, Wrapper } from './styles';
import { defaultTheme, popoverTheme, suggestionsTheme } from './theme';
import { CommentInputProps } from './types';

const ARROW_UP = 'ArrowUp';
const ARROW_DOWN = 'ArrowDown';
const ARROW_LEFT = 'ArrowLeft';
const ARROW_RIGHT = 'ArrowRight';
const ENTER = 'Enter';
const SUGGESTIONS_LENGTH = 3;

const CommentInput: React.FC<CommentInputProps> = (props) => {
  const [selectionStart, setSelectionStart] = React.useState<number>(
    props.value.length
  );
  const [selectionEnd, setSelectionEnd] = React.useState<number>(
    props.value.length
  );
  const [prevValue, setPrevValue] = React.useState<string>('');
  const [items, setItems] = React.useState<Item[]>([]);
  const [lastMention, setLastMention] = React.useState<MentionItem | null>(
    null
  );
  const [lastMentionPosition, setLastMentionPosition] = React.useState<
    number | null
  >(null);

  const [suggestions, setSuggestions] = React.useState<Mention[]>([]);
  const [activeSuggestion, setActiveSuggestion] = React.useState<number | null>(
    null
  );
  const [suggestionListPlacement, setSuggestionListPlacement] = React.useState<
    PopoverPlacement.Bottom | PopoverPlacement.Top
  >(PopoverPlacement.Bottom);

  const ref = useRef<HTMLTextAreaElement>(null);

  const itemsAbortRef = useRef<AbortController>();
  const suggestionsAbortRef = useRef<AbortController>();
  const [suggestionsLoading, setSuggestionsLoading] =
    React.useState<boolean>(false);
  const { value, onSearch, getUserMention, onItemsChange } = props;

  const [suggestionListRef, suggestionListInView] = useIntersectionObserver({
    threshold: 1,
  });

  const getSearchString = useCallback((query: string) => {
    const lowerCaseText = query.toLowerCase();
    return lowerCaseText.startsWith('@')
      ? lowerCaseText.substring(1)
      : lowerCaseText;
  }, []);

  const getUser = useCallback(
    async (slug: string): Promise<Mention | null> => {
      const searchString = getSearchString(slug);
      if (!searchString) {
        return null;
      }

      return await getUserMention(searchString);
    },
    [getSearchString, getUserMention]
  );

  const handleClose = useCallback(() => {
    setSuggestions([]);
    setActiveSuggestion(null);
  }, []);

  // Text area drop selection[Start/End] if value changed outside
  useEffect(() => {
    if (ref.current) {
      ref.current.selectionStart = selectionStart;
      ref.current.selectionEnd = selectionEnd;
    }
  }, [selectionStart, selectionEnd]);

  const searchSuggestions = useCallback(
    async (text: string, signal?: AbortSignal) => {
      const searchString = getSearchString(text);
      const results = await onSearch(searchString);

      if (!signal?.aborted && results) {
        const suggestions = results?.slice(0, SUGGESTIONS_LENGTH);
        setSuggestions(suggestions);
        setSuggestionsLoading(false);
      }
    },
    [onSearch, getSearchString]
  );

  const debouncedUpdateSuggestions = useDebounce({
    callback: searchSuggestions,
    delay: 500,
    waitForPromises: true,
  });

  const updateSuggestions = useCallback(
    (position: number, newItems: Item[], signal: AbortSignal): void => {
      const caretMention = getItemInPosition(position, 'mention', newItems);

      const caretMentionPosition = findItemPositionBorder(
        position,
        newItems,
        'mention',
        'start'
      );

      const differentMention =
        lastMentionPosition !== caretMentionPosition &&
        (!caretMention || !!caretMention.mention);

      if (
        differentMention ||
        caretMentionPosition === null ||
        caretMention?.value === '@'
      ) {
        setSuggestions([]);
        setActiveSuggestion(null);
        setLastMention(caretMention);
        setLastMentionPosition(caretMentionPosition);
        setSuggestionsLoading(false);
        return;
      }

      if (caretMention && caretMention.value !== lastMention?.value) {
        setSuggestionsLoading(true);
        debouncedUpdateSuggestions(caretMention.value, signal);
        if (!signal.aborted) {
          setLastMention(caretMention);
        }
      } else {
        setSuggestionsLoading(false);
      }

      setLastMentionPosition(caretMentionPosition);
    },
    [debouncedUpdateSuggestions, lastMention?.value, lastMentionPosition]
  );

  const updateItems = useCallback(
    async (
      newValue: string,
      signal: AbortSignal,
      suggestionsSignal: AbortSignal
    ): Promise<void> => {
      const splitted = splitByMentions(newValue);
      const converted = convertIntoItems(splitted);

      const matched = await matchMentionItems(converted, getUser);
      if (!signal.aborted) {
        setItems(matched);
        onItemsChange(matched);
        updateSuggestions(
          ref.current?.selectionStart ?? newValue.length,
          matched,
          suggestionsSignal
        );
      }
    },
    [getUser, updateSuggestions, onItemsChange]
  );

  useEffect(() => {
    if (value !== prevValue) {
      setPrevValue(value);
      itemsAbortRef.current?.abort();
      itemsAbortRef.current = new AbortController();
      suggestionsAbortRef.current?.abort();
      suggestionsAbortRef.current = new AbortController();
      void updateItems(
        value,
        itemsAbortRef.current?.signal,
        suggestionsAbortRef.current?.signal
      );
    }
  }, [prevValue, updateItems, value]);

  const handleChange = async (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): Promise<void> => {
    itemsAbortRef.current?.abort();
    itemsAbortRef.current = new AbortController();
    suggestionsAbortRef.current?.abort();
    suggestionsAbortRef.current = new AbortController();

    const newInputValue = event.target.value;
    props.onChange(newInputValue);
    if (ref.current) {
      setSelectionStart(ref.current.selectionStart);
      setSelectionEnd(ref.current.selectionEnd);
    }

    // do not need to calculate items twice
    setPrevValue(newInputValue);
    await updateItems(
      newInputValue,
      itemsAbortRef.current?.signal,
      suggestionsAbortRef.current?.signal
    );
  };

  // Choose suggestion in the suggestions list
  const handleVerticalArrowClick = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    event.preventDefault();

    let nextSuggestion;
    if (activeSuggestion !== null) {
      const offset = event.key === ARROW_DOWN ? 1 : -1;
      nextSuggestion =
        (activeSuggestion + offset + suggestions.length) % suggestions.length;
    } else {
      nextSuggestion = event.key === ARROW_DOWN ? 0 : suggestions.length - 1;
    }

    setActiveSuggestion(nextSuggestion);
  };

  const handleHorizontalArrowClick = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    event.preventDefault();
    const offset = event.key === ARROW_RIGHT ? 1 : -1;

    if (ref.current) {
      const side =
        event.key === ARROW_RIGHT
          ? ref.current.selectionEnd
          : ref.current.selectionStart;

      const next = Math.max(side + offset, 0);
      const newSelectionStart = Math.min(value.length, next);
      setSelectionStart(newSelectionStart);
      setSelectionEnd(newSelectionStart);

      suggestionsAbortRef.current?.abort();
      suggestionsAbortRef.current = new AbortController();
      updateSuggestions(
        newSelectionStart,
        items,
        suggestionsAbortRef.current?.signal
      );
    }
  };

  // Select suggestion from a suggestion list and move cursor in the end
  // of the selected slug
  const handleEnterClick = (
    event?: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    event?.preventDefault();

    const mention = suggestions[activeSuggestion ?? 0];
    const newItem: MentionItem = {
      type: 'mention',
      value: `@${mention.slug}`,
      mention: mention,
    };
    const position = ref.current?.selectionStart ?? 0;
    let newItems = updateItemInPosition(position, items, newItem);

    let newSelectionStart =
      findItemPositionBorder(position, newItems, 'any', 'end') ?? 0;

    const nextItem = getItemInPosition(newSelectionStart + 1, 'text', newItems);
    if (nextItem && !nextItem.value[0].match(afterSeparators)) {
      newItems = updateItemInPosition(newSelectionStart + 1, newItems, {
        type: 'text',
        value: ` ${nextItem.value}`,
      });
      newSelectionStart++;
    }

    if (!nextItem) {
      newItems.push({ type: 'text', value: ' ' });
      newSelectionStart++;
    }

    const newText = getText(newItems);
    props.onChange(newText);
    setItems(newItems);
    props.onItemsChange(newItems);
    // do not need to calculate items twice
    setPrevValue(newText);
    setActiveSuggestion(null);
    setSuggestions([]);
    setLastMention(newItem);
    setLastMentionPosition(null);

    if (ref.current) {
      ref.current.focus();
      setSelectionStart(newSelectionStart);
      setSelectionEnd(newSelectionStart);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (suggestions.length && event.key === ENTER) {
      handleEnterClick(event);
      return;
    }

    if (
      suggestions.length &&
      (event.key === ARROW_UP || event.key === ARROW_DOWN)
    ) {
      handleVerticalArrowClick(event);
      return;
    }

    if (
      !event.shiftKey &&
      (event.key === ARROW_LEFT || event.key === ARROW_RIGHT)
    ) {
      handleHorizontalArrowClick(event);
    }

    props.onKeyDown(event);
  };

  const handleClick = (): void => {
    suggestionsAbortRef.current?.abort();
    suggestionsAbortRef.current = new AbortController();
    if (ref.current?.selectionStart === ref.current?.selectionEnd) {
      updateSuggestions(
        ref.current?.selectionStart ?? 0,
        items,
        suggestionsAbortRef.current?.signal
      );
    }
    if (ref.current) {
      setSelectionStart(ref.current.selectionStart);
      setSelectionEnd(ref.current.selectionEnd);
    }
  };

  const open = suggestionsLoading || suggestions.length > 0;
  const theme = { ...defaultTheme, ...props.theme };

  useEffect(() => {
    if (!suggestionListInView) {
      setSuggestionListPlacement(PopoverPlacement.Top);
    }
  }, [suggestionListInView]);

  useEffect(() => {
    if (open) {
      setSuggestionListPlacement(PopoverPlacement.Bottom);
    }
  }, [open]);

  return (
    <Popover
      hideTip
      align={PopoverAlign.Start}
      container={props.wrapperRef}
      content={
        <SuggestionList
          activeSuggestion={activeSuggestion}
          loading={suggestionsLoading}
          suggestions={suggestions}
          suggestionsRef={suggestionListRef}
          theme={suggestionsTheme}
          onClick={(_: unknown): void => handleEnterClick()}
          onSetActive={setActiveSuggestion}
        />
      }
      open={open}
      placement={suggestionListPlacement}
      showHeader={false}
      theme={popoverTheme}
      width="294px"
      onWillClose={handleClose}
    >
      <Wrapper style={props.style} theme={theme}>
        <Input
          ref={ref}
          data-testid="design-post--comments-input"
          disabled={props.disabled}
          placeholder={props.placeholder}
          theme={theme}
          value={value}
          onChange={handleChange}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        />
      </Wrapper>
    </Popover>
  );
};

export default withEventScope(CommentInput, { module: 'commentInput' });
